/**
 * Page-scoped editing, the Pages analog of editing in a state.
 *
 * While page editing is on for a Pages container and one of its pages shows,
 * an edit to the container's SHARED content (a shared child, or anything
 * inside one) belongs to that page: it is written into that page's state on
 * the edited object, not into Base. The first edit keys the object for the
 * page: a state named after the page, and the page's `states` assignment
 * targeting the object's container-relative path. Content of a page is plain
 * authored content, because it exists on that page only.
 */

import type { SceneObject } from '../../core/SceneObject';
import { Pages } from '../../behaviors/pages';
import { Properties } from '../../behaviors/properties';
import type { ValidationError } from '../../scenedata/behaviordata';
import type { PageData } from '../../scenedata/behaviors/pagesdata';
import type { StateDefinition } from '../../scenedata/behaviors/propertiesdata';
import type { UndoCommand } from '../undo/UndoCommand';
import { AddBehaviorCommand } from '../undo/AddBehaviorCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import { bundle, rollBack } from '../authoring/steps';

/**
 * Whether page editing is on for `container`, a Pages container: the host's
 * mode is on, and the container's context is a page rather than its shared
 * content as a whole. Only the host knows either.
 */
export type PageEditing = (container: SceneObject) => boolean;

/** Where an edit to shared content goes while a page shows. */
export interface PageCaptureContext {
    container: SceneObject;
    pages: Pages;
    /** The edited object: a shared child, or anything inside one. */
    object: SceneObject;
    /** `object`'s path relative to the container. */
    path: string;
    pageIndex: number;
    pageName: string;
    /** How many pages the container has. */
    pageCount: number;
    /** The state this page already assigns `object`, if any. */
    assignedState: string | null;
}

/**
 * Where an edit to `object` goes for the page that shows, or null when it goes
 * to Base: page editing is off for the container, the scene plays, `object` is
 * not shared content of a Pages container, or no single page shows (all of
 * them do, or the container takes its pages from its first child).
 */
export function pageCaptureContext(object: SceneObject, pageEditing: PageEditing): PageCaptureContext | null {
    const scene = object.traceScene;
    if (!scene || scene.isPlayMode()) return null;

    // The container's direct child the object sits under.
    let root = object;
    let container = root.getParent();
    while (container && !container.getBehavior(Pages)) {
        root = container;
        container = container.getParent();
    }
    const pages = container?.getBehavior(Pages);
    if (!container || !pages || !pageEditing(container)) return null;

    const data = pages.getData();
    // Membership by resolution, as at runtime: only entries that resolve to the root count.
    if (data.pageSource === 'firstChild' || !data.shared?.some(path => scene.resolveSceneObject(path, container) === root)) {
        return null;
    }
    if (pages.isShowingAll()) return null;
    const pageIndex = pages.getActivePageIndex();
    const pageNodes = pages.getPageNodes();
    if (pageIndex < 0 || pageIndex >= pageNodes.length) return null;
    const path = scene.getRelativePath(object, container);
    if (!path) return null;

    return {
        container,
        pages,
        object,
        path,
        pageIndex,
        pageName: pageNodes[pageIndex].name,
        pageCount: pageNodes.length,
        assignedState: data.pages?.[pageIndex]?.states?.find(assignment => assignment.target === path)?.state ?? null,
    };
}

/**
 * Write `mutate`'s change into the page's state on the context's object, and
 * return the undo command for all of it, already applied: adding Properties
 * if the object has none, the state, and the page's assignment. The state
 * then shows on the object, and the container reports a change. A fault part
 * way takes back what was done before it is thrown.
 *
 * The state is the one the page assigns the object. One that another page
 * assigns too (a duplicated page copies its assignments) is forked first, so
 * the edit stays on this page: a new state named after the page, starting as
 * a copy, which this page is then assigned instead. With no state yet, it
 * starts empty.
 */
export async function capturePageState(
    ctx: PageCaptureContext,
    label: string,
    mutate: (state: StateDefinition) => StateDefinition,
): Promise<UndoCommand> {
    const commands: UndoCommand[] = [];
    try {
        let props = ctx.object.getBehavior(Properties);
        if (!props) {
            props = await ctx.object.addBehavior(Properties);
            commands.push(new AddBehaviorCommand(ctx.object, 'Properties', props.getData()));
        }

        const before = props.getData();
        const states = { ...before.states };
        const assigned = ctx.assignedState;
        const fork = assigned !== null && (ctx.pages.getData().pages ?? []).some((page, index) =>
            index !== ctx.pageIndex && page?.states?.some(a => a.target === ctx.path && a.state === assigned));
        const name = assigned !== null && !fork ? assigned : freshStateName(ctx.pageName, states);
        const current = states[name] ?? (fork && states[assigned] ? structuredClone(states[assigned]) : { values: {} });
        states[name] = mutate(current);
        refused('Properties', await props.commit({ ...before, states }));
        commands.push(new BehaviorCommitCommand(ctx.object, 'Properties', before, props.getData()));

        if (assigned !== name) {
            const pagesBefore = ctx.pages.getData();
            refused('Pages', await ctx.pages.commit({ ...pagesBefore, pages: assign(pagesBefore.pages, ctx, name) }));
            commands.push(new BehaviorCommitCommand(ctx.container, 'Pages', pagesBefore, ctx.pages.getData()));
        }

        // Show the page's state, which may be a new fork; the live pose already matches it.
        props.setState(name, true);
    } catch (error) {
        await rollBack(commands, error);
        throw error;
    }
    dispatchBehaviorChanged({ objectPath: ctx.container.getPath(), behaviorType: 'Pages', action: 'updated' });
    return bundle(label, commands);
}

/** Throw when a commit refused its patch, so what was done before it is taken back. */
function refused(type: string, errors: readonly ValidationError[]): void {
    if (errors.length > 0) throw new Error(`[capturePageState] ${type} refused the patch: ${errors.map(error => error.message).join('; ')}`);
}

/** The page's name, or the first of "Name 2", "Name 3"… no state has. */
function freshStateName(pageName: string, states: Record<string, unknown>): string {
    let name = pageName;
    for (let n = 2; name in states; n++) name = `${pageName} ${n}`;
    return name;
}

/** `pages` with the context's page assigning `state` to its object, one entry per page and none trailing empty. */
function assign(pages: readonly PageData[] | undefined, ctx: PageCaptureContext, state: string): PageData[] | undefined {
    const next = Array.from({ length: ctx.pageCount }, (_, index) => pages?.[index] ?? {});
    const page = next[ctx.pageIndex];
    next[ctx.pageIndex] = { ...page, states: [...(page.states ?? []).filter(a => a.target !== ctx.path), { target: ctx.path, state }] };
    while (next.length > 0 && !next[next.length - 1].states) next.pop();
    return next.length > 0 ? next : undefined;
}
